import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useServerApi } from "./userServerApi";

export const NOTIFICATION_INBOX_REFRESH = "notification:inbox-refresh";
const INBOX_REFRESH_INTERVAL_MS = 15000;
const SOCKET_RECONCILE_DELAY_MS = 400;

export const requestNotificationInboxRefresh = (detail = {}) => {
  window.dispatchEvent(new CustomEvent(NOTIFICATION_INBOX_REFRESH, { detail }));
};

const NotificationInboxContext = createContext(null);

const useNotificationInboxState = ({ enabled = true } = {}) => {
  const auth = useAuthUser();
  const [api] = useServerApi();
  const apiRef = useRef(api);
  const refreshInFlightRef = useRef(false);
  const reconcileTimerRef = useRef(null);
  apiRef.current = api;

  const user = auth();
  const userId = user?.id || user?._id;

  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshUnreadCount = useCallback(async () => {
    if (!enabled || !userId) return;
    try {
      const res = await apiRef.current.Notification.getUnreadCount();
      if (res?.data) setUnreadCount(res.data.count ?? 0);
    } catch (error) {
      console.log("refreshUnreadCount failed:", error.message);
    }
  }, [enabled, userId]);

  const refreshRecent = useCallback(async () => {
    if (!enabled || !userId) return;
    setLoading(true);
    try {
      const res = await apiRef.current.Notification.getInbox({ page: 1, limit: 5 });
      if (res?.data?.docs) setRecent(res.data.docs);
    } catch (error) {
      console.log("refreshRecent failed:", error.message);
    } finally {
      setLoading(false);
    }
  }, [enabled, userId]);

  const refreshAll = useCallback(async () => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      await Promise.all([refreshUnreadCount(), refreshRecent()]);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [refreshUnreadCount, refreshRecent]);

  const markAllRead = useCallback(async () => {
    const res = await apiRef.current.Notification.markAllRead();
    if (res?.success) {
      setUnreadCount(0);
      await refreshRecent();
    }
    return res;
  }, [refreshRecent]);

  const markRead = useCallback(
    async (id) => {
      const res = await apiRef.current.Notification.markRead(id);
      if (res?.success) {
        await refreshAll();
      }
      return res;
    },
    [refreshAll]
  );

  // Initial load — only when userId becomes available
  useEffect(() => {
    if (!enabled || !userId) {
      setUnreadCount(0);
      setRecent([]);
      return;
    }
    refreshAll();
  }, [enabled, userId, refreshAll]);

  // Fallback refresh in case the socket event is missed or the room join is delayed.
  useEffect(() => {
    if (!enabled || !userId) return undefined;
    const interval = window.setInterval(
      () => requestNotificationInboxRefresh(),
      INBOX_REFRESH_INTERVAL_MS
    );
    return () => window.clearInterval(interval);
  }, [enabled, userId]);

  // Socket / manual refresh events
  useEffect(() => {
    if (!enabled) return;
    const handleRefresh = (event) => {
      const increment = event?.detail?.incrementUnread;
      if (increment) {
        setUnreadCount((count) => count + increment);
      }
      if (reconcileTimerRef.current) {
        window.clearTimeout(reconcileTimerRef.current);
      }
      reconcileTimerRef.current = window.setTimeout(() => {
        refreshAll();
        reconcileTimerRef.current = null;
      }, SOCKET_RECONCILE_DELAY_MS);
    };
    window.addEventListener(NOTIFICATION_INBOX_REFRESH, handleRefresh);
    return () => {
      window.removeEventListener(NOTIFICATION_INBOX_REFRESH, handleRefresh);
      if (reconcileTimerRef.current) {
        window.clearTimeout(reconcileTimerRef.current);
        reconcileTimerRef.current = null;
      }
    };
  }, [enabled, refreshAll]);

  return {
    unreadCount,
    recent,
    loading,
    refreshUnreadCount,
    refreshRecent,
    refreshAll,
    markAllRead,
    markRead,
  };
};

export const NotificationInboxProvider = ({ children }) => {
  const value = useNotificationInboxState();
  return (
    <NotificationInboxContext.Provider value={value}>
      {children}
    </NotificationInboxContext.Provider>
  );
};

export const useNotificationInbox = () => {
  const value = useContext(NotificationInboxContext);
  if (!value) {
    throw new Error(
      "useNotificationInbox must be used within NotificationInboxProvider"
    );
  }
  return value;
};
